import { of, tap } from "rxjs";
import { IOrgOption } from "../../common/models/license.interface";
import { Store } from "@ngrx/store";
import { inject, Injectable } from "@angular/core";
import { AppState } from "../../common/state/root";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, mergeMap, switchMap, take, withLatestFrom } from "rxjs/operators";
import { LicenseV2Actions } from "./subscriptions-plus.actions";
import { LicenseActions } from "../../common/state/license/license.actions";
import { selectLoggedInUser } from "../../common/state/user/user.selectors";
import { selectIncludeChildren } from "./subscriptions-plus.selector";
import { LicenseV2Service } from "../service/subscriptions-plus.service";
import { ToastService } from "../../common/services/toast/toast.service";
import { ToastType } from "../../common/models";

const INVALID_USER_ERROR = Error("Logged in user not valid");
const INVALID_USER_ERROR_RESPONSE = { error: INVALID_USER_ERROR };

@Injectable()
export class LicenseV2Effects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<AppState>);
    private licenseV2Service$ = inject(LicenseV2Service);
    private toastService = inject(ToastService);

    loadLicenses$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLicenses),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadLicensesFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.getLicenses(action.includeChildren ?? false).pipe(
                map(licenses => LicenseV2Actions.loadLicensesSuccess({ licenses })),
                catchError(error => of(LicenseV2Actions.loadLicensesFailure({ error })))
            );
        })
    ));

    getLicenseById$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.getLicenseById),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.getLicenseByIdFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.getLicenseById(action.licenseId).pipe(
                map(license => LicenseV2Actions.getLicenseByIdSuccess({ license })),
                catchError(error => of(LicenseV2Actions.getLicenseByIdFailure({ error })))
            );
        })
    ));

    addLicense$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.addLicense),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.addLicenseFailure(INVALID_USER_ERROR_RESPONSE));
            }

            const license = { ...action.license, organisationId: loggedInUser.organisation_id };
            return this.licenseV2Service$.addLicense(license).pipe(
                map(licenses => LicenseV2Actions.addLicenseSuccess({ licenses })),
                catchError(error => of(LicenseV2Actions.addLicenseFailure({ error })))
            );
        })
    ));

    updateLicense$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.updateLicense),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.updateLicenseFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.updateLicense(action.license).pipe(
                map(license => LicenseV2Actions.updateLicenseSuccess({ license })),
                catchError(error => of(LicenseV2Actions.updateLicenseFailure({ error })))
            );
        })
    ));

    deleteLicenses$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.deleteLicenses),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.deleteLicensesFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.deleteLicenses(action.ids).pipe(
                map(licenses => LicenseV2Actions.deleteLicensesSuccess({ licenses })),
                catchError(error => of(LicenseV2Actions.deleteLicensesFailure({ error })))
            );
        })
    ));

    bulkTransition$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.bulkTransition),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.bulkTransitionFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.bulkTransition(action.action, action.ids, action.prices).pipe(
                map(response => LicenseV2Actions.bulkTransitionSuccess({ action: action.action, response })),
                catchError(error => of(LicenseV2Actions.bulkTransitionFailure({ error })))
            );
        })
    ));

    bulkEditLicenses$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.bulkEditLicenses),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.bulkEditLicensesFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.bulkEditLicenses(action.ids, action.changes).pipe(
                map(response => LicenseV2Actions.bulkEditLicensesSuccess({ response })),
                catchError(error => of(LicenseV2Actions.bulkEditLicensesFailure({ error })))
            );
        })
    ));

    bulkEditLicensesSuccessReload$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.bulkEditLicensesSuccess),
        withLatestFrom(this.store$.select(selectIncludeChildren)),
        map(([, includeChildren]) => LicenseV2Actions.loadLicenses({ includeChildren }))
    ));

    bulkEditLicensesFailureToast$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.bulkEditLicensesFailure),
        tap(() => this.toastService.showToast('Failed to bulk edit licenses', ToastType.ERROR))
    ), { dispatch: false });

    loadLicenseUsage$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLicenseUsage),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        // mergeMap (not switchMap): multiple licenses' usage can be in flight at once
        // (e.g. several table rows loading concurrently) — switchMap would cancel
        // an earlier license's request the moment a later one is dispatched.
        // Concurrency capped at 5 so a page with many metered licenses visible at
        // once (grouped view can show up to 50 rows) doesn't fire 50+ requests together.
        mergeMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadLicenseUsageFailure({ licenseId: action.licenseId, ...INVALID_USER_ERROR_RESPONSE }));
            }

            return this.licenseV2Service$.getLicenseUsage(action.licenseId).pipe(
                map(usage => LicenseV2Actions.loadLicenseUsageSuccess({ licenseId: action.licenseId, usage })),
                catchError(error => of(LicenseV2Actions.loadLicenseUsageFailure({ licenseId: action.licenseId, error })))
            );
        }, 5)
    ));

    distributeLicenses$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.distributeLicenses),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.distributeLicensesFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.distributeLicenses(action.instructions).pipe(
                map(response => LicenseV2Actions.distributeLicensesSuccess({ response })),
                catchError(error => of(LicenseV2Actions.distributeLicensesFailure({ error })))
            );
        })
    ));

    loadLicenseLinks$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLicenseLinks),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadLicenseLinksFailure(INVALID_USER_ERROR_RESPONSE));
            }

            if (action.licenseIds.length === 0) {
                return of(LicenseV2Actions.loadLicenseLinksSuccess({ linksByLicense: [] }));
            }

            return this.licenseV2Service$.getBatchLicenseLinks(action.licenseIds).pipe(
                map(linksByLicense => LicenseV2Actions.loadLicenseLinksSuccess({ linksByLicense })),
                catchError(error => of(LicenseV2Actions.loadLicenseLinksFailure({ error })))
            );
        })
    ));

    loadGlobalLinks$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadGlobalLinks),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadGlobalLinksFailure(INVALID_USER_ERROR_RESPONSE));
            }

            if (action.licenseIds.length === 0) {
                return of(LicenseV2Actions.loadGlobalLinksSuccess({ linksByLicense: [] }));
            }

            return this.licenseV2Service$.getBatchLicenseLinks(action.licenseIds).pipe(
                map(linksByLicense => LicenseV2Actions.loadGlobalLinksSuccess({ linksByLicense })),
                catchError(error => of(LicenseV2Actions.loadGlobalLinksFailure({ error })))
            );
        })
    ));

    loadLinksOverview$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLinksOverview),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        // mergeMap: the page-wide batch and a dialog's single-license fetch can be
        // in flight together — switchMap would cancel the page-wide request.
        mergeMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadLinksOverviewFailure(INVALID_USER_ERROR_RESPONSE));
            }

            if (action.licenseIds.length === 0) {
                return of(LicenseV2Actions.loadLinksOverviewSuccess({ linksByLicense: [] }));
            }

            return this.licenseV2Service$.getBatchLicenseLinks(action.licenseIds).pipe(
                map(linksByLicense => LicenseV2Actions.loadLinksOverviewSuccess({ linksByLicense })),
                catchError(error => of(LicenseV2Actions.loadLinksOverviewFailure({ error })))
            );
        })
    ));

    loadLinksOverviewOnLicensesLoad$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLicensesSuccess),
        map(({ licenses }) => LicenseV2Actions.loadLinksOverview({ licenseIds: licenses.map(l => l.id) }))
    ));

    bulkManageLinks$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.bulkManageLinks),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.bulkManageLinksFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.bulkManageLinks(action.pairs).pipe(
                map(response => LicenseV2Actions.bulkManageLinksSuccess({ response })),
                catchError(error => of(LicenseV2Actions.bulkManageLinksFailure({ error })))
            );
        })
    ));

    refreshLicensesAfterBulkLink$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.bulkManageLinksSuccess),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([, loggedInUser]) => [
            LicenseActions.fetchUsersLicenses(),
            LicenseActions.fetchV2Licenses(),
            LicenseV2Actions.loadLicenses({ includeChildren: loggedInUser?.distribution != null }),
        ])
    ));

    loadLicenseNotes$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLicenseNotes),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadLicenseNotesFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.getNotesByLicense(action.licenseId).pipe(
                map(notes => LicenseV2Actions.loadLicenseNotesSuccess({ licenseId: action.licenseId, notes })),
                catchError(error => of(LicenseV2Actions.loadLicenseNotesFailure({ error })))
            );
        })
    ));

    createLicenseNote$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.createLicenseNote),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.createLicenseNoteFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.createNote(action.request).pipe(
                map(note => LicenseV2Actions.createLicenseNoteSuccess({ note })),
                catchError(error => of(LicenseV2Actions.createLicenseNoteFailure({ error })))
            );
        })
    ));

    deleteLicenseNote$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.deleteLicenseNote),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.deleteLicenseNoteFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.deleteNotes([action.noteId]).pipe(
                map(() => LicenseV2Actions.deleteLicenseNoteSuccess({ noteId: action.noteId })),
                catchError(error => of(LicenseV2Actions.deleteLicenseNoteFailure({ error })))
            );
        })
    ));

    unlinkFromOrganisation$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.unlinkFromOrganisation),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.unlinkFromOrganisationFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.bulkManageLinks(action.pairs).pipe(
                map(response => LicenseV2Actions.unlinkFromOrganisationSuccess({ response })),
                catchError(error => of(LicenseV2Actions.unlinkFromOrganisationFailure({ error })))
            );
        })
    ));

    refreshLicensesAfterUnlink$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.unlinkFromOrganisationSuccess),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([, loggedInUser]) => [
            LicenseActions.fetchUsersLicenses(),
            LicenseActions.fetchV2Licenses(),
            LicenseV2Actions.loadLicenses({ includeChildren: loggedInUser?.distribution != null }),
        ])
    ));

    unlinkFromOrganisationSuccessToast$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.unlinkFromOrganisationSuccess),
        tap(() => this.toastService.showToast('License disabled on organisation', ToastType.SUCCESS))
    ), { dispatch: false });

    unlinkFromOrganisationFailureToast$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.unlinkFromOrganisationFailure),
        tap(() => this.toastService.showToast('Failed to disable license on organisation', ToastType.ERROR))
    ), { dispatch: false });


    loadChildOrganisations$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadChildOrganisations),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadChildOrganisationsFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.getChildOrganisations().pipe(
                map(childOrgs => LicenseV2Actions.loadChildOrganisationsSuccess({ childOrgs: childOrgs ?? [] })),
                catchError(error => of(LicenseV2Actions.loadChildOrganisationsFailure({ error })))
            );
        })
    ));

    loadChildOrganisationsFailureToast$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadChildOrganisationsFailure),
        tap(() => this.toastService.showToast('Failed to load child organisations', ToastType.ERROR))
    ), { dispatch: false });

    loadLinkEntityData$ = createEffect(() => this.actions$.pipe(
        ofType(LicenseV2Actions.loadLinkEntityData),
        withLatestFrom(this.store$.select(selectLoggedInUser)),
        switchMap(([action, loggedInUser]) => {
            if (loggedInUser == null) {
                return of(LicenseV2Actions.loadLinkEntityDataFailure(INVALID_USER_ERROR_RESPONSE));
            }

            return this.licenseV2Service$.getLicenseLinks(action.licenseId).pipe(
                take(1),
                map(links => {
                    const currentlyLinkedOrgId = links.find(l => l.entityType === 'organisation')?.entityId ?? null;
                    const orgOptions: IOrgOption[] = (loggedInUser.organisation_id && loggedInUser.organisation?.name)
                        ? [{ id: loggedInUser.organisation_id, name: loggedInUser.organisation.name }]
                        : [];
                    return LicenseV2Actions.loadLinkEntityDataSuccess({ currentlyLinkedOrgId, orgOptions });
                }),
                catchError(error => of(LicenseV2Actions.loadLinkEntityDataFailure({ error })))
            );
        })
    ));
}
